import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useSession } from '@/composables/useSession';

// Base path for the SPA — `/app/` in both dev (Vite) and prod (nginx).
// Keeping this consistent means URLs like `/app/login` resolve identically
// in both environments and vue-router never has to "strip" the prefix
// (which previously caused a `//` redirect when the catchAll fell back to `/`).
const base = import.meta.env.BASE_URL;

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'index',
    component: () => import('@/views/IndexView.vue'),
  },
  {
    path: '/md/:filename(.*)',
    name: 'md',
    component: () => import('@/views/MdView.vue'),
    props: true,
  },
  {
    path: '/papers/:filename',
    name: 'pdf',
    component: () => import('@/views/PdfView.vue'),
    props: true,
  },
  {
    path: '/notes/:filename(.*)/history',
    name: 'note-history',
    component: () => import('@/views/NoteHistoryView.vue'),
    props: true,
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/search',
    name: 'search',
    component: () => import('@/views/SearchResultsView.vue'),
  },
  {
    path: '/workshops',
    name: 'workshops',
    component: () => import('@/views/WorkshopsView.vue'),
  },
  {
    path: '/workshops/:projectId',
    name: 'workshop-project',
    component: () => import('@/views/WorkshopProjectView.vue'),
    props: true,
  },
  {
    path: '/workshops/:projectId/:filePath(.*)',
    name: 'workshop-file',
    component: () => import('@/views/WorkshopProjectView.vue'),
    props: true,
  },
  {
    // Unknown paths: bounce to the index within the SPA. Resolves to
    // `<base>/`, so in prod this is `/app/` and stays inside the SPA
    // mount (does not escape to the Jinja fallback at `/`).
    path: '/:catchAll(.*)',
    redirect: '/',
  },
];

export const router = createRouter({
  history: createWebHistory(base),
  routes,
});

const { isAuthenticated, state, refresh } = useSession();

router.beforeEach(async (to) => {
  // Public routes always pass.
  if (to.meta.public) return true;

  // First navigation after page load: probe session.
  if (!to.meta.public && !to.meta.sessionChecked) {
    const ok = await refresh();
    if (!ok && to.name !== 'login') {
      return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (to.meta.requiresAdmin && !state.isAdmin) {
      return { name: 'index' };
    }
    to.meta.sessionChecked = true;
    return true;
  }

  if (!isAuthenticated.value && to.name !== 'login') {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.meta.requiresAdmin && !state.isAdmin) {
    return { name: 'index' };
  }
  return true;
});
